import { useCallback, useState } from "react";
import { AlertCircle, CircleCheck, Info, Loader2 } from "lucide-react";
import {
  PIPEDREAM_PROVIDER_ID,
  VaultSync,
  VaultSyncProviderId,
  VaultSyncProviderIdSchema,
} from "@forgepoint/shared";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ConnectionSelector } from "@/components/ConnectionSelector";
import { DriveBrowser, DriveSelection } from "./driveBrowser/DriveBrowser";
import {
  VAULT_SYNC_PROVIDER_LABELS,
  useVaultSyncsStore,
} from "./vaultSyncsStore";

const DEFAULT_PROVIDER_ID: VaultSyncProviderId = "microsoft_onedrive";

const ACCOUNT_LABELS: Record<VaultSyncProviderId, string> = {
  microsoft_onedrive: "Microsoft 365",
  sharepoint: "Microsoft 365",
  box: "Box",
};

interface AddSyncedFolderSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  parentFolderId: string | null;
  destinationName: string;
  onCreated: (sync: VaultSync) => void;
}

/**
 * Slide-over for linking a cloud drive folder (or whole drive) into Vault:
 * picks the source, a connection, and the upstream folder, then creates the sync.
 * @param props - Open state, the Vault folder the sync lands in, and the created callback
 * @returns The add-synced-folder sheet element
 */
export function AddSyncedFolderSheet({
  open,
  onOpenChange,
  parentFolderId,
  destinationName,
  onCreated,
}: AddSyncedFolderSheetProps) {
  const createSync = useVaultSyncsStore((state) => state.createSync);
  const isCreating = useVaultSyncsStore((state) => state.isCreating);
  const createError = useVaultSyncsStore((state) => state.createError);
  const clearCreateError = useVaultSyncsStore(
    (state) => state.clearCreateError,
  );
  const [providerId, setProviderId] =
    useState<VaultSyncProviderId>(DEFAULT_PROVIDER_ID);
  const [connectionId, setConnectionId] = useState<string | null>(null);
  const [selection, setSelection] = useState<DriveSelection | null>(null);
  const [isReconnectRequired, setIsReconnectRequired] = useState(false);
  const [selectorKey, setSelectorKey] = useState(0);
  const providerLabel = VAULT_SYNC_PROVIDER_LABELS[providerId];

  const close = () => {
    setProviderId(DEFAULT_PROVIDER_ID);
    setConnectionId(null);
    setSelection(null);
    setIsReconnectRequired(false);
    clearCreateError();
    onOpenChange(false);
  };

  const selectConnection = useCallback((nextConnectionId: string) => {
    setConnectionId(nextConnectionId);
    setSelection(null);
    setIsReconnectRequired(false);
  }, []);

  const requireReconnect = useCallback(() => {
    setIsReconnectRequired(true);
    setConnectionId(null);
    setSelection(null);
    setSelectorKey((key) => key + 1);
  }, []);

  const selectProvider = (value: string) => {
    const parsed = VaultSyncProviderIdSchema.safeParse(value);
    if (!parsed.success) {
      return;
    }
    setProviderId(parsed.data);
    setConnectionId(null);
    setSelection(null);
    setIsReconnectRequired(false);
  };

  const submit = async () => {
    if (!connectionId || !selection) {
      return;
    }
    const sync = await createSync({
      connectionId,
      driveId: selection.driveId,
      itemId: selection.itemId,
      parentFolderId,
    });
    if (sync) {
      onCreated(sync);
      close();
    }
  };

  return (
    <Sheet
      open={open}
      onOpenChange={(next) => (next ? onOpenChange(true) : close())}
    >
      <SheetContent className="flex w-full flex-col gap-0 overflow-hidden p-0 sm:max-w-xl">
        <SheetHeader className="border-b px-6 py-4">
          <SheetTitle>Add synced folder</SheetTitle>
          <SheetDescription>
            Pick a source, connection, and folder. Files sync into{" "}
            {destinationName}.
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 space-y-6 overflow-y-auto px-6 py-5">
          <div className="space-y-4">
            <div className="grid gap-2">
              <Label htmlFor="vault-sync-source">Source</Label>
              <Select value={providerId} onValueChange={selectProvider}>
                <SelectTrigger id="vault-sync-source">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {VaultSyncProviderIdSchema.options.map((id) => (
                    <SelectItem key={id} value={id}>
                      {VAULT_SYNC_PROVIDER_LABELS[id]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid gap-2">
              <Label>Connection</Label>
              <ConnectionSelector
                key={`${providerId}-${selectorKey}`}
                appId={providerId}
                providerId={PIPEDREAM_PROVIDER_ID}
                connectionId={connectionId}
                onSelect={selectConnection}
              />
              {isReconnectRequired && (
                <Alert variant="destructive">
                  <AlertCircle className="size-4" />
                  <AlertTitle>Reconnect {providerLabel}</AlertTitle>
                  <AlertDescription>
                    {ACCOUNT_LABELS[providerId]} rejected this connection.
                    Choose Create Connection above and sign in again.
                  </AlertDescription>
                </Alert>
              )}
            </div>

            <div className="grid gap-2">
              <Label>Folder</Label>
              {connectionId ? (
                <>
                  <p className="text-sm text-muted-foreground">
                    Open the folder you want to sync.
                  </p>
                  <DriveBrowser
                    key={connectionId}
                    connectionId={connectionId}
                    providerId={providerId}
                    selection={selection}
                    onSelect={setSelection}
                    onReconnectRequired={requireReconnect}
                  />
                  {selection && (
                    <p
                      key={`${selection.driveId}-${selection.itemId}`}
                      aria-live="polite"
                      className="flex items-center gap-2 text-sm duration-200 motion-safe:animate-in motion-safe:fade-in"
                    >
                      <CircleCheck className="size-4 shrink-0 text-primary" />
                      <span className="min-w-0 break-words">
                        Will sync{" "}
                        <span className="font-medium">
                          {selection.location}
                        </span>
                      </span>
                    </p>
                  )}
                </>
              ) : (
                <p className="text-sm text-muted-foreground">
                  Select a connection to choose a folder.
                </p>
              )}
            </div>
          </div>

          <div className="flex items-start gap-2 text-sm text-muted-foreground">
            <Info className="mt-0.5 h-4 w-4 shrink-0" />
            <span>
              Files sync every 5 minutes and are read-only in Vault. Everyone in
              your organization with Vault access will see them.
            </span>
          </div>

          {createError && (
            <Alert variant="destructive">
              <AlertCircle className="size-4" />
              <AlertDescription>{createError}</AlertDescription>
            </Alert>
          )}
        </div>

        <SheetFooter className="border-t px-6 py-4">
          <Button variant="outline" onClick={close} disabled={isCreating}>
            Cancel
          </Button>
          <Button
            disabled={!connectionId || !selection || isCreating}
            onClick={submit}
          >
            Add synced folder
            {isCreating && <Loader2 className="size-4 animate-spin" />}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
